import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Close,
  TaskAlt,
  ConfirmationNumber,
  Timeline,
  Map as MapIcon,
  History as HistoryIcon,
  CircleOutlined,
  PlayCircleOutline,
  HourglassEmpty
} from '@mui/icons-material';
import { api } from '../../services/api';
import { formatDate, formatStoredDate } from '../../utils/formatters';

interface ChangelogItem {
  id: string;
  type: 'task' | 'ticket';
  title: string;
  date: string; // ISO date string
  displayDate: string; // Formatted date
  status: string;
}

interface ChangelogModalProps {
  open: boolean;
  onClose: () => void;
}

const ChangelogModal: React.FC<ChangelogModalProps> = ({ open, onClose }) => {
  const [completedItems, setCompletedItems] = useState<ChangelogItem[]>([]);
  const [roadmapItems, setRoadmapItems] = useState<ChangelogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // 1. Search for project ID
      const searchData = (await api.get('/search', {
        params: { q: 'Internal Portal Development' }
      })).data;
      const projectResult = searchData.data?.find((r: any) =>
        r.type === 'project' && r.title === 'Internal Portal Development'
      );

      if (!projectResult) {
        throw new Error('Project "Internal Portal Development" not found');
      }

      const projectId = projectResult.id;

      // 2. Fetch Project Tasks
      const projectData = (await api.get(`/projects/${projectId}`)).data;

      const tasks = projectData.success && projectData.data && projectData.data.tasks
        ? projectData.data.tasks
        : [];

      // 3. Fetch Project Tickets
      const ticketsData = (await api.get('/tickets', {
        params: { project_id: projectId }
      })).data;
      const tickets = ticketsData.success && Array.isArray(ticketsData.data)
        ? ticketsData.data
        : [];

      // 4. Process Completed items (Changelog)
      const completedTasks = tasks.filter((t: any) =>
        t.status === 'completed'
      ).map((t: any) => ({
        id: t.id,
        type: 'task' as const,
        title: t.title,
        date: t.completed_date || t.updated_at,
        displayDate: formatDate(t.completed_date || t.updated_at),
        status: t.status
      }));

      const completedTickets = tickets.filter((t: any) =>
        t.status === 'resolved' || t.status === 'closed'
      ).map((t: any) => ({
        id: t.id,
        type: 'ticket' as const,
        title: `${t.ticket_number} - ${t.subject}`,
        date: t.resolved_at || t.closed_at || t.updated_at,
        displayDate: formatDate(t.resolved_at || t.closed_at || t.updated_at),
        status: t.status
      }));

      let allCompleted = [...completedTasks, ...completedTickets];
      allCompleted.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setCompletedItems(allCompleted);

      // 5. Process Roadmap items (Pending)
      const pendingTasks = tasks.filter((t: any) =>
        ['todo', 'in_progress', 'review'].includes(t.status)
      ).map((t: any) => ({
        id: t.id,
        type: 'task' as const,
        title: t.title,
        date: t.due_date || t.created_at,
        displayDate: t.due_date ? `Due: ${formatStoredDate(t.due_date)}` : `Created: ${formatDate(t.created_at)}`,
        status: t.status
      }));

      const pendingTickets = tickets.filter((t: any) =>
        ['open', 'in_progress', 'pending_customer'].includes(t.status)
      ).map((t: any) => ({
        id: t.id,
        type: 'ticket' as const,
        title: `${t.ticket_number} - ${t.subject}`,
        date: t.sla_due_date || t.created_at,
        displayDate: t.sla_due_date ? `Due: ${formatDate(t.sla_due_date)}` : `Created: ${formatDate(t.created_at)}`,
        status: t.status
      }));

      let allPending = [...pendingTasks, ...pendingTickets];
      allPending.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setRoadmapItems(allPending);

    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError('Could not load data at this time.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const currentItems = activeTab === 0 ? completedItems : roadmapItems;

  const getStatusIcon = (status: string, type: 'task' | 'ticket') => {
    if (type === 'task') {
      if (status === 'completed') return <TaskAlt color="success" fontSize="small" />;
      if (status === 'in_progress') return <PlayCircleOutline color="info" fontSize="small" />;
      return <CircleOutlined color="action" fontSize="small" />; // todo
    } else {
      // ticket
      if (['resolved', 'closed'].includes(status)) return <ConfirmationNumber color="success" fontSize="small" />;
      if (status === 'in_progress') return <HourglassEmpty color="warning" fontSize="small" />;
      return <ConfirmationNumber color="primary" fontSize="small" />; // open
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').toUpperCase();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: 0,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timeline color="primary" />
          <Typography variant="h6" component="div">
            Project Updates
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab icon={<HistoryIcon sx={{ mr: 1 }} />} iconPosition="start" label="Changelog" />
          <Tab icon={<MapIcon sx={{ mr: 1 }} />} iconPosition="start" label="Roadmap" />
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 0, py: 0, flexGrow: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>{error}</Typography>
          </Box>
        ) : currentItems.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>No items found.</Typography>
          </Box>
        ) : (
          <List sx={{ pt: 0 }}>
            {currentItems.map((item, index) => (
              <React.Fragment key={item.id}>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    px: 3,
                    py: 2,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
                    {getStatusIcon(item.status, item.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 500 }}>
                          {item.title}
                        </Typography>
                        <Chip
                          label={item.displayDate}
                          size="small"
                          variant="outlined"
                          sx={{ flexShrink: 0, fontSize: '0.75rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={getStatusLabel(item.status)}
                          size="small"
                          variant="filled"
                          color={activeTab === 0 ? "success" : "default"}
                          sx={{
                            fontSize: '0.65rem',
                            height: 20,
                            borderRadius: 0.5
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600, ml: 1 }}>
                          {item.type}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < currentItems.length - 1 && (
                  <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', mx: 3 }} />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangelogModal;
